import { useCallback, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useStatuses } from "@/modules/inventory/hooks/useStatuses";
import { engineKeys } from "@/modules/inventory/hooks/useEngines";
import { trailerKeys } from "@/modules/inventory/hooks/useTrailers";
import { engineApi, type AssetWritePayload } from "@/modules/inventory/services/engineApi";
import { trailerApi } from "@/modules/inventory/services/trailerApi";
import type { Engine } from "@/modules/inventory/types/engine.types";
import type { Trailer } from "@/modules/inventory/types/trailer.types";
import type { AssetType } from "../asset-form/asset-form-config";
import { ASSET_MONEY_KEYS, stripMoneyKeys } from "@/modules/inventory/lib/moneyFields";
import { useCan } from "@/modules/tenant/hooks/useTenantAuth";

export interface AssetDraftSyncReturn {
  assetId: string | null;
  isCreating: boolean;
  isSaving: boolean;
  lastSavedAt: Date | null;
  saveError: string | null;
  /** Null until the statuses catalog has loaded. */
  draftStatusId: string | null;
  /**
   * Get-or-create the server draft. Returns the asset id, or null when the
   * Draft status hasn't loaded yet / the create failed (caller surfaces the
   * toast). Concurrent calls share one in-flight create (double-drop safe).
   */
  ensureDraft: () => Promise<string | null>;
  /** Auto-save: PATCH partial data, re-asserting status_id=Draft. Non-blocking. */
  saveDraft: (data: AssetWritePayload) => Promise<void>;
  /** Final PATCH with the user's chosen status — flips the asset out of Draft. */
  publishDraft: (data: AssetWritePayload) => Promise<Engine | Trailer>;
  resetDraft: () => void;
  setAssetId: (id: string) => void;
}

/**
 * SP3-full slice 1 (D4) — the asset-canvas fork of useWizardDraftSync, over
 * the SP2-D engine/trailer write endpoints. Same lifecycle contract as the
 * boat wizard:
 *  - ensureDraft creates the row with status_id=Draft only — the backend
 *    auto-generates the EN-/TR- stock # (D2) and the InventoryItem observer
 *    derives website_status='disabled'.
 *  - every auto-save re-asserts status_id=Draft so a half-finished canvas
 *    session can't drift out of draft through field-level patches.
 *  - publishDraft PATCHes the final payload (with the user's status) and is
 *    the only write that leaves Draft.
 */
export function useAssetDraftSync(type: AssetType): AssetDraftSyncReturn {
  const [assetId, setAssetId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Shared in-flight create: a second drop while the first create is pending
  // must NOT create a second draft (mirror LiveFillCanvas.ensureDraft).
  const createInFlightRef = useRef<Promise<string | null> | null>(null);
  // The live id for callbacks that run before the assetId state flush.
  const assetIdRef = useRef<string | null>(null);

  const queryClient = useQueryClient();
  // #1408 (D-08) — a second layer behind asset-form-config's buildPayload: a
  // viewer without finance.cost.view never sends cost_price, invoice or freight.
  const canViewCost = useCan("finance.cost.view");

  const { data: statusesResponse } = useStatuses();
  const draftStatusId = useMemo(() => {
    const items = statusesResponse?.items ?? [];
    return items.find((s) => s.is_system === true && s.value === "draft")?.id ?? null;
  }, [statusesResponse]);

  const api = useMemo(
    () =>
      type === "engine"
        ? { create: engineApi.createEngine, update: engineApi.updateEngine }
        : { create: trailerApi.createTrailer, update: trailerApi.updateTrailer },
    [type],
  );

  const invalidate = useCallback(
    (id?: string) => {
      const keys = type === "engine" ? engineKeys : trailerKeys;
      queryClient.invalidateQueries({ queryKey: keys.lists() });
      if (id) queryClient.invalidateQueries({ queryKey: keys.detail(id) });
    },
    [queryClient, type],
  );

  const ensureDraft = useCallback(async (): Promise<string | null> => {
    if (assetIdRef.current) return assetIdRef.current;
    if (createInFlightRef.current) return createInFlightRef.current;
    if (!draftStatusId) {
      setSaveError("Draft status not yet loaded — try again in a moment");
      return null;
    }

    setIsCreating(true);
    setSaveError(null);

    const inFlight = (async () => {
      try {
        const created = await api.create({ status_id: draftStatusId });
        const id = String((created as Engine | Trailer).id ?? "");
        if (!id) throw new Error("No asset id returned from server");

        assetIdRef.current = id;
        setAssetId(id);
        setLastSavedAt(new Date());
        invalidate();
        return id;
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : "Failed to create draft");
        return null;
      } finally {
        setIsCreating(false);
        createInFlightRef.current = null;
      }
    })();

    createInFlightRef.current = inFlight;
    return inFlight;
  }, [api, draftStatusId, invalidate]);

  const saveDraft = useCallback(
    async (data: AssetWritePayload): Promise<void> => {
      const id = assetIdRef.current;
      if (!id || !draftStatusId) return;

      setIsSaving(true);
      try {
        await api.update(id, { ...stripMoneyKeys(data, ASSET_MONEY_KEYS, canViewCost), status_id: draftStatusId });
        setLastSavedAt(new Date());
        setSaveError(null);
        invalidate(id);
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : "Failed to save draft");
      } finally {
        setIsSaving(false);
      }
    },
    [api, draftStatusId, invalidate, canViewCost],
  );

  const publishDraft = useCallback(
    async (data: AssetWritePayload): Promise<Engine | Trailer> => {
      const id = assetIdRef.current;
      if (!id) throw new Error("No server draft to publish");

      const saved = await api.update(id, stripMoneyKeys(data, ASSET_MONEY_KEYS, canViewCost));
      invalidate(id);
      return saved;
    },
    [api, invalidate, canViewCost],
  );

  const resetDraft = useCallback(() => {
    assetIdRef.current = null;
    createInFlightRef.current = null;
    setAssetId(null);
    setIsCreating(false);
    setIsSaving(false);
    setLastSavedAt(null);
    setSaveError(null);
  }, []);

  const setAssetIdExternal = useCallback((id: string) => {
    assetIdRef.current = id;
    setAssetId(id);
  }, []);

  return {
    assetId,
    isCreating,
    isSaving,
    lastSavedAt,
    saveError,
    draftStatusId,
    ensureDraft,
    saveDraft,
    publishDraft,
    resetDraft,
    setAssetId: setAssetIdExternal,
  };
}
